<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import { resetPassword, ApiError } from '../lib/admin';

  // The token IS the credential; it rides in the link the email sent.
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  let password = $state('');
  let confirm = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);

  const tooShort = $derived(password.length > 0 && password.length < 8);
  const mismatch = $derived(confirm.length > 0 && confirm !== password);
  const canSubmit = $derived(!!token && password.length >= 8 && confirm === password && !busy);

  async function onSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    busy = true;
    error = null;
    try {
      await resetPassword(token, password);
      navigate('/admin'); // reset signs us in — land straight in the dashboard
    } catch (err: unknown) {
      error =
        err instanceof ApiError && err.status === 400
          ? 'This reset link is invalid, expired, or already used. Request a new one.'
          : err instanceof ApiError && err.status === 0
            ? 'Couldn’t reach the server — check your connection and try again.'
            : err instanceof Error
              ? err.message
              : 'Something went wrong. Please try again.';
    } finally {
      busy = false;
    }
  }
</script>

<div class="console-glow flex min-h-screen items-center justify-center px-6">
  <div class="w-full max-w-sm">
    <div class="mb-8 text-center">
      <img src="/icon-128.png" alt="" class="mx-auto mb-4 h-11 w-11 rounded-xl" />
      <div class="eyebrow mb-2">Account recovery</div>
      <h1 class="font-display text-2xl font-bold tracking-tight">Set a new password</h1>
    </div>

    <div class="rounded-box border hair bg-base-200/60 p-6 shadow-2xl shadow-black/40">
      {#if !token}
        <div class="flex flex-col gap-4 text-center">
          <p class="text-sm text-base-content/80">
            This link is missing its token. Request a fresh reset link and try again.
          </p>
          <button class="btn btn-primary btn-sm" onclick={() => navigate('/forgot-password')}>
            Request a new link
          </button>
        </div>
      {:else}
        <form class="flex flex-col gap-4" onsubmit={onSubmit}>
          <label class="block">
            <span class="eyebrow mb-1.5 block">New password</span>
            <input
              type="password"
              class="input input-bordered w-full bg-base-100/60 font-mono text-sm"
              bind:value={password}
              autocomplete="new-password"
              placeholder="At least 8 characters"
              required
            />
            {#if tooShort}
              <span class="mt-1 block text-xs text-warning">Use at least 8 characters.</span>
            {/if}
          </label>
          <label class="block">
            <span class="eyebrow mb-1.5 block">Confirm password</span>
            <input
              type="password"
              class="input input-bordered w-full bg-base-100/60 font-mono text-sm"
              bind:value={confirm}
              autocomplete="new-password"
              placeholder="••••••••"
              required
            />
            {#if mismatch}
              <span class="mt-1 block text-xs text-warning">Passwords don’t match.</span>
            {/if}
          </label>

          {#if error}
            <div class="rounded-field border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">
              {error}
            </div>
          {/if}

          <button type="submit" class="btn btn-primary mt-1 gap-2 font-semibold" disabled={!canSubmit}>
            {#if busy}<span class="loading loading-spinner loading-sm"></span>{/if}
            Set password &amp; sign in
          </button>
        </form>
        <div class="mt-4 text-center">
          <button class="eyebrow link" onclick={() => navigate('/admin/login')}>Back to sign in</button>
        </div>
      {/if}
    </div>

    <p class="eyebrow mt-5 text-center">Sideguide · content control</p>
  </div>
</div>
